import {
  CHART_NOTES_BILLING_TYPE,
  CHART_NOTES_GENERAL_TYPE,
  CHART_NOTES_VISIT_TYPE
} from "constants/chart-notes";
import { useGetMergeTagsLastUserChartNoteQuery } from "services/merge-tags";

import { type LastUserChartNotesByType } from "./merge-tags-data-source.type";

type UseLastUserChartNotesByTypeParams = {
  patientId?: number;
  userId?: number;
  excludeChartNoteId?: number;
  skip: boolean;
};

/**
 * Prefetches the current user's most recent chart note for the patient, one per
 * chart-note type (plus "any"), so the "My Last Chart Note" merge tag can resolve
 * synchronously for whichever type the user selected, e.g. {{Patient.MyLastChartNote(type=visit)}}.
 */
export const useLastUserChartNotesByType = ({
  patientId,
  userId,
  excludeChartNoteId,
  skip
}: UseLastUserChartNotesByTypeParams) => {
  const baseArg = {
    patientId: Number(patientId),
    userId: Number(userId),
    excludeChartNoteId
  };
  const queryOptions = { skip };

  const anyNote = useGetMergeTagsLastUserChartNoteQuery(baseArg, queryOptions);
  const visitNote = useGetMergeTagsLastUserChartNoteQuery(
    { ...baseArg, type: CHART_NOTES_VISIT_TYPE },
    queryOptions
  );
  const generalNote = useGetMergeTagsLastUserChartNoteQuery(
    { ...baseArg, type: CHART_NOTES_GENERAL_TYPE },
    queryOptions
  );
  const billingNote = useGetMergeTagsLastUserChartNoteQuery(
    { ...baseArg, type: CHART_NOTES_BILLING_TYPE },
    queryOptions
  );

  const byType: LastUserChartNotesByType = {
    any: anyNote.data,
    visit: visitNote.data,
    general: generalNote.data,
    billing: billingNote.data
  };

  const isLoading =
    anyNote.isLoading ||
    visitNote.isLoading ||
    generalNote.isLoading ||
    billingNote.isLoading;

  const isFetching =
    anyNote.isFetching ||
    visitNote.isFetching ||
    generalNote.isFetching ||
    billingNote.isFetching;

  return { byType, isLoading, isFetching };
};
